"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDismissOnOutside = void 0;
const react_1 = require("react");
const util_1 = require("@app/util");
/**
 * Dismisses an open overlay on Escape and on a press outside all of `insideRefs`.
 *
 * The repo has no click-outside hook and no shared Escape handler — `Modal` owns its own
 * `keydown` listener and nothing else does — so this is where the sidebar's usage panel gets
 * both. Neither listener exists while `isActive` is false.
 *
 * **Pass the trigger as well as the overlay.** The listener is on `mousedown`, not `click`,
 * because the click that opened the overlay is still travelling up to the document when the
 * listener is attached and would close it again in the same gesture. The consequence is that a
 * trigger left out of `insideRefs` would be closed on mousedown and re-opened by its own
 * click, so the toggle would never appear to close.
 */
const useDismissOnOutside = (isActive, insideRefs, onDismiss) => {
    // Read through refs so a fresh array literal or callback on every render does not tear the
    // document listeners down and add them straight back.
    const insideRefsRef = (0, react_1.useRef)(insideRefs);
    insideRefsRef.current = insideRefs;
    const onDismissRef = (0, react_1.useRef)(onDismiss);
    onDismissRef.current = onDismiss;
    (0, react_1.useEffect)(() => {
        if (!isActive)
            return;
        const handleMouseDown = (event) => {
            const { target } = event;
            if (!(target instanceof Node))
                return;
            const isInside = insideRefsRef.current.some((ref) => (0, util_1.isValue)(ref.current) && ref.current.contains(target));
            if (!isInside)
                onDismissRef.current();
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape')
                onDismissRef.current();
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isActive]);
};
exports.useDismissOnOutside = useDismissOnOutside;
exports.default = exports.useDismissOnOutside;
